import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useStore } from '../../context/Context';
import Image from '../fragments/Image';
import { Link } from 'react-router-dom';
import Footer from '../global/Footer';

const Project = () => {
    const { posts, pageLoaded, appError } = useStore();

    const projectHeaderRef = useRef(null);
    const projectRef = useRef(null);
    const slideRefs = useRef([]);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [infoOpen, setInfoOpen] = useState(false);

    const handleResize = useCallback(() => {
        if (projectHeaderRef.current) {
            const height = projectHeaderRef.current.clientHeight;
            document.documentElement.style.setProperty('--project-header-height', `${height}px`);
        }
    }, []);

    const handleScroll = useCallback(() => {
        if (!slideRefs.current.length || !projectRef.current) return;

		const firstSlide = slideRefs.current[0];
		

		if (firstSlide) {

			const firstSlideHeight = firstSlide.clientHeight;

			let scrollTop = window.pageYOffset || document.documentElement.scrollTop;
			if (scrollTop < firstSlideHeight && infoOpen === true) {
				scrollTop = firstSlideHeight;
				window.scrollTo(0, scrollTop);
			}

			const windowHeight = window.innerHeight;
			const viewportCenter = scrollTop + windowHeight / 2;

            const firstSlideTop = firstSlide.getBoundingClientRect().top + scrollTop;

            if (scrollTop > firstSlideHeight) {
                projectRef.current.classList.add('state-scrolled');
            } else {
                projectRef.current.classList.remove('state-scrolled');
            }

            // Find which slide is most in view
            let newCurrentIndex = 0;
            let maxVisibleArea = 0;

            slideRefs.current.forEach((slide, index) => {
                if (!slide) return;

                if (index !== 0) {
                    const rect = slide.getBoundingClientRect();
                    const slideTop = rect.top + scrollTop;
                    const slideBottom = slideTop + rect.height;

                    // Calculate visible area of this slide
                    const visibleTop = Math.max(slideTop, scrollTop);
                    const visibleBottom = Math.min(slideBottom, scrollTop + windowHeight);
                    const visibleArea = Math.max(0, visibleBottom - visibleTop);

                    if (visibleArea > maxVisibleArea) {
                        maxVisibleArea = visibleArea;
                        newCurrentIndex = index;
                    }
                }
            });

            if (scrollTop < firstSlideHeight) {
                newCurrentIndex = 0;
            }

            if (newCurrentIndex !== currentIndex) {
                setCurrentIndex(newCurrentIndex);
            }
        }
    }, [currentIndex]);

    const toggleInfo = useCallback(() => {
        
		if (infoOpen === false) {
			const firstSlide = slideRefs.current[0];
		
			if (firstSlide) {

				const firstSlideHeight = firstSlide.clientHeight;

				let scrollTop = window.pageYOffset || document.documentElement.scrollTop;
				if (scrollTop < firstSlideHeight) {
					scrollTop = firstSlideHeight;
					window.scrollTo({top: scrollTop, behavior: 'smooth'});
                    setTimeout(() => {
                        document.documentElement.style.overflow = 'hidden';
			            document.body.style.overflow = 'hidden';
                    }, 500);
				}
			}
		} else {
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
        }
		setInfoOpen(prevState => !prevState);
        setTimeout(() => {
            handleResize(); 
            handleScroll(); 
    	}, 0);
	}, [handleResize, handleScroll]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            handleResize();
        }, 0);

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll);
        
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleResize, handleScroll]);

    useEffect(() => {
        if (posts && posts.length > 0) {
            const timeoutId = setTimeout(() => {
                handleResize();
                handleScroll(); 
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [posts, infoOpen, handleResize, handleScroll]);

    useEffect(() => {
        const post = posts[0];
        if (post?.slides?.length) {
            const hash = window.location.hash; // e.g. "#slide-2"
            if (hash.startsWith('#slide-')) {
                const index = parseInt(hash.replace('#slide-', ''), 10) - 1;
                console.log('index from hash', index);
                const slideEl = slideRefs.current[index];
                setTimeout(() => {
                    if (slideEl) {
                        console.log('scrolling to slide', index, slideEl);
                        const topPos = getOffsetTop(slideEl) - document.getElementsByClassName('c-header')[0].clientHeight - 1 - projectHeaderRef.current.clientHeight;
                        window.scrollTo({ top: topPos, behavior: 'auto' });
                        setCurrentIndex(index); // update current slide
                    }
                }, 100);
            }
        }
    }, [posts]);

    function getOffsetTop(element) {
        let offsetTop = 0;
        while (element) {
            offsetTop += element.offsetTop;
            element = element.offsetParent;
        }
        return offsetTop;
    }

    if (appError) return null;
    if (!posts || posts.length === 0) return null;
    
    const post = posts[0];
    
    if (!post?.slides) return null;

    return (
        <div className="c-page__block">
            <div className={`c-project ${infoOpen ? 'state-info-open' : ''}`} ref={projectRef}>

                <div className="c-project__header">
                    <div className="c-project__header__inner" ref={projectHeaderRef}>
                        <span className="c-project__header__counter" dangerouslySetInnerHTML={{ __html: '(' + (currentIndex + 1) + '/' + post.slides.length + ')' }}></span>
                        <h2 className="c-project__header__title" dangerouslySetInnerHTML={{ __html: post.title }}></h2>
                        <h3 className="c-project__header__credit" dangerouslySetInnerHTML={{ __html: post.credit }}></h3>
                        <span 
                            className="c-project__header__information" 
                            dangerouslySetInnerHTML={{ __html: "Project Information" }} 
                            onClick={toggleInfo}
                        ></span>
                    </div>
                    <div className="c-project__header__content">
                        <div className="c-project__header__content__inner">
                            <div className="scope-text scope-text--faded" dangerouslySetInnerHTML={{ __html: post.information }} />
                            {post.media && post.media.length > 0 && (
                                <>
                                    <h3 className="c-project__header__content__heading">Media</h3>
                                    <ul className="c-project__header__media">
                                        {post.media.map((mediaItem, index) => (
                                            <React.Fragment key={index}>
                                                {mediaItem.url ? (
                                                    <li className="c-project__header__media__item">
                                                        <a 
                                                            className="c-project__header__media__item__inner c-project__header__media__item__inner--link" 
                                                            href={mediaItem.url} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer" 
                                                            dangerouslySetInnerHTML={{ __html: '<span>' + mediaItem.title + '</span><span>' + mediaItem.year + '</span>' }}
                                                        ></a>
                                                    </li>
                                                ) : (
                                                    <li className="c-project__header__media__item">
                                                        <span 
                                                            className="c-project__header__media__item__inner" 
                                                            dangerouslySetInnerHTML={{ __html: '<span>' + mediaItem.title + '</span><span>' + mediaItem.year + '</span>' }}
                                                        ></span>
                                                    </li>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                
                {post.slides && post.slides.length > 0 && (
                    <>
                        {post.slides.map((slide, index) => {
                            if (slide.type === 'inset') {
                                return (
                                    <div 
                                        className={`c-project__slide c-project__slide--index-${index} c-project__slide--inset ${slide.width < slide.height ? 'c-project__slide--portrait' : ''}`} 
                                        key={index}
                                        ref={el => slideRefs.current[index] = el}
                                    >
                                        <div className="c-project__slide__inner">
                                            <Image src={slide.src} width={slide.width} height={slide.height} alt={slide.alt} />
                                        </div>
                                    </div>
                                );
                            } else if (slide.type === 'wide') {
                                return (
                                    <div 
                                        className={`c-project__slide c-project__slide--index-${index} c-project__slide--wide ${slide.width < slide.height ? 'c-project__slide--portrait' : ''}`} 
                                        key={index}
                                        ref={el => slideRefs.current[index] = el}
                                    >
                                        <div className="c-project__slide__inner">
                                            <Image src={slide.src} width={slide.width} height={slide.height} alt={slide.alt} />
                                        </div>
                                    </div>
                                );
                            } else if (slide.type === 'two_columns') {
                                return (
                                    <div 
                                        className={`c-project__slide c-project__slide--index-${index} c-project__slide--two-columns`} 
                                        key={index}
                                        ref={el => slideRefs.current[index] = el}
                                    >
                                        <div className="c-project__slide__inner">
                                            <div className="c-project__slide__column c-project__slide__column--left">
                                                <Image src={slide.left_src} width={slide.left_width} height={slide.left_height} alt={slide.left_alt} />
                                            </div>
                                            <div className="c-project__slide__column c-project__slide__column--right">
                                                <Image src={slide.right_src} width={slide.right_width} height={slide.right_height} alt={slide.right_alt} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                            
                            // Return null for unknown slide types
                            return null;
                        })}
                    </>
                )}
                
            </div>

            <Footer post={post} />
        </div>
    );
};

export default Project;